import React, { useEffect } from 'react';
import coopImg from '../assets/chick_coop.jpg';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  BuildingStorefrontIcon,
  ShoppingCartIcon,
  ShieldCheckIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BellAlertIcon,
  ChatBubbleBottomCenterTextIcon,
  CameraIcon
} from '@heroicons/react/24/outline';

const Landing = () => {
  // Load JivoChat widget only on landing page
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//code.jivosite.com/widget/eTsZyNbC3p';
    script.async = true;
    script.id = 'jivo-script';
    document.body.appendChild(script);

    return () => {
      // Thorough JivoChat cleanup when leaving the page
      
      // 1. Use JivoChat API to close/destroy if available
      if (window.jivo_api) {
        try { window.jivo_api.close(); } catch (e) {}
      }
      if (window.jivo_destroy) {
        try { window.jivo_destroy(); } catch (e) {}
      }

      // 2. Remove the script we added
      const jivoScript = document.getElementById('jivo-script');
      if (jivoScript) jivoScript.remove();

      // 3. Remove all JivoChat DOM elements (iframes, containers, etc.)
      const jivoSelectors = [
        '[id^="jivo"]',
        '[class^="jivo"]',
        '[class*=" jivo"]',
        'jdiv',
        '[class^="__jivoMobileButton"]',
      ];
      jivoSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => el.remove());
      });

      // 4. Clean up global JivoChat variables
      const jivoGlobals = Object.keys(window).filter(key => 
        key.startsWith('jivo') || key.startsWith('__jivo')
      );
      jivoGlobals.forEach(key => {
        try { delete window[key]; } catch (e) { window[key] = undefined; }
      });
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-primary-50 via-white to-white">
      {/* decorative background blobs */}
      <div className="pointer-events-none absolute -top-24 -left-16 h-72 w-72 rounded-full bg-primary-200 blur-3xl opacity-30"></div>
      <div className="pointer-events-none absolute top-40 -right-16 h-96 w-96 rounded-full bg-green-200 blur-3xl opacity-30"></div>

      <header className="relative max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 via-green-500 to-yellow-500 text-white grid place-items-center shadow-lg">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C10.9 2 10 2.9 10 4C10 4.7 10.4 5.4 11 5.7V7C11 7.6 11.4 8 12 8S13 7.6 13 7V5.7C13.6 5.4 14 4.7 14 4C14 2.9 13.1 2 12 2ZM12 9C8.7 9 6 11.7 6 15V16C6 16.6 6.4 17 7 17H8V19C8 20.1 8.9 21 10 21H14C15.1 21 16 20.1 16 19V17H17C17.6 17 18 16.6 18 16V15C18 11.7 15.3 9 12 9ZM14 19H10V17H14V19ZM16 15H8C8 12.8 9.8 11 12 11S16 12.8 16 15Z"/>
              </svg>
            </div>
          </div>
          <span className="text-xl md:text-2xl font-semibold text-gray-900">Omabra Poultry Assistant</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/academy" className="text-sm text-gray-700 hover:text-gray-900 font-medium">Academy</Link>
          <Link to="/login" className="text-sm text-gray-700 hover:text-gray-900">Sign in</Link>
          <Link to="/register" className="btn-primary text-sm">Get Started</Link>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-6 pt-10 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
              Run your farm with clarity — powered by AI
            </h1>
            <p className="mt-5 text-lg text-gray-600 max-w-xl">
              Omabra keeps your records tidy, schedules on time, and guidance at hand.
              Simple tools, smart insights, less guesswork.
            </p>
            <div className="mt-7 flex items-center gap-3">
              <Link to="/register" className="btn-primary">Start free trial</Link>
              <a href="#pricing" className="btn-outline">Pricing</a>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <Feature title="AI Assistant" desc="Advice & image diagnosis for your flock"/>
              <Feature title="Smart Reminders" desc="Vaccination, feeding, and tasks on time"/>
              <Feature title="Records & Analytics" desc="Eggs, feed, sales, costs — clear insights"/>
              <Feature title="Market" desc="Order feed, equipment, chicks"/>
            </div>
          </div>
          <div className="relative rounded-2xl bg-white shadow-xl border border-gray-100 overflow-hidden">
            <img
              alt="Chicken coop with dashboard stats overlay"
              className="w-full h-[460px] object-cover"
              src={coopImg}
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                <Stat label="Total Chickens" value="100"/>
                <Stat label="Pending Activities" value="15"/>
                <Stat label="This Month's Profit" value="KES 20,000"/>
              </div>
            </div>
          </div>
        </div>

        {/* Ecosystem Section */}
        <section id="ecosystem" className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">A Complete Poultry Ecosystem</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Omabra connects farmers, suppliers, and customers in one seamless platform — 
              making it easier to manage, buy, and sell poultry products.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Farmers */}
            <div className="relative rounded-2xl border border-green-100 bg-gradient-to-br from-green-50 to-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-14 w-14 rounded-xl bg-green-600 text-white grid place-items-center mb-4">
                <UserGroupIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">For Farmers</h3>
              <p className="text-gray-600 mb-4">
                Manage your farm, track records, and sell directly to customers with your own online shop.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <ChartBarIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Track eggs, birds, feed, and expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <BellAlertIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Smart reminders for vaccinations & tasks</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>AI assistant for diagnosis & advice</span>
                </li>
                <li className="flex items-start gap-2">
                  <BuildingStorefrontIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Create your own online shop</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShoppingCartIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span>Order supplies from local suppliers</span>
                </li>
              </ul>
              <Link to="/register?type=farmer" className="mt-6 block w-full text-center py-2.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors">
                Start as Farmer
              </Link>
            </div>

            {/* Suppliers */}
            <div className="relative rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-14 w-14 rounded-xl bg-blue-600 text-white grid place-items-center mb-4">
                <BuildingStorefrontIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">For Suppliers</h3>
              <p className="text-gray-600 mb-4">
                Reach farmers in your area. List your products and get orders directly from poultry farmers.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <TruckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span>Sell feed, chicks, equipment & more</span>
                </li>
                <li className="flex items-start gap-2">
                  <UserGroupIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span>Connect with local farmers</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChartBarIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span>Track orders and revenue</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span>Secure payments via escrow</span>
                </li>
                <li className="flex items-start gap-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span>Get paid when orders are delivered</span>
                </li>
              </ul>
              <Link to="/register?type=supplier" className="mt-6 block w-full text-center py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors">
                Join as Supplier
              </Link>
            </div>

            {/* Customers */}
            <div className="relative rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-14 w-14 rounded-xl bg-amber-600 text-white grid place-items-center mb-4">
                <ShoppingCartIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">For Customers</h3>
              <p className="text-gray-600 mb-4">
                Buy fresh eggs and poultry products directly from local farms. Know where your food comes from.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <BuildingStorefrontIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span>Browse local farm shops</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShoppingCartIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span>Order eggs, chicken & more</span>
                </li>
                <li className="flex items-start gap-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span>Pay securely via M-Pesa or wallet</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheckIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span>Escrow protection until delivery</span>
                </li>
                <li className="flex items-start gap-2">
                  <TruckIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span>Track orders & confirm delivery</span>
                </li>
              </ul>
              <Link to="/register?type=client" className="mt-6 block w-full text-center py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors">
                Shop as Customer
              </Link>
            </div>
          </div>
        </section>

        {/* How It Works - Escrow Flow */}
        <section id="how-it-works" className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Secure Payments with Escrow Protection</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Your money is protected. Payments are held in escrow until you confirm delivery.
            </p>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-green-200 via-blue-200 to-amber-200 -translate-y-1/2 z-0"></div>
            
            <div className="grid md:grid-cols-5 gap-4 relative z-10">
              <FlowStep 
                number="1" 
                title="Place Order" 
                desc="Customer orders from farm shop"
                color="green"
              />
              <FlowStep 
                number="2" 
                title="Farm Confirms" 
                desc="Farmer confirms the order"
                color="green"
              />
              <FlowStep 
                number="3" 
                title="Pay Securely" 
                desc="Payment held in escrow"
                color="blue"
              />
              <FlowStep 
                number="4" 
                title="Delivery" 
                desc="Farmer ships order"
                color="blue"
              />
              <FlowStep 
                number="5" 
                title="Confirm & Release" 
                desc="Customer confirms, payment released"
                color="amber"
              />
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-xl p-5 border border-green-100">
              <ShieldCheckIcon className="h-8 w-8 text-green-600 mb-3" />
              <h4 className="font-semibold text-gray-900">Buyer Protection</h4>
              <p className="text-sm text-gray-600 mt-1">
                Money stays in escrow until you confirm you've received your order.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
              <CurrencyDollarIcon className="h-8 w-8 text-blue-600 mb-3" />
              <h4 className="font-semibold text-gray-900">Seller Guarantee</h4>
              <p className="text-sm text-gray-600 mt-1">
                Payments are guaranteed once delivery is confirmed by the buyer.
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
              <TruckIcon className="h-8 w-8 text-amber-600 mb-3" />
              <h4 className="font-semibold text-gray-900">Auto-Release</h4>
              <p className="text-sm text-gray-600 mt-1">
                Payment automatically released after 7 days if buyer doesn't respond.
              </p>
            </div>
          </div>
        </section>

        {/* AI Features */}
        <section id="ai-features" className="mt-20">
          <div className="rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-700 p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold">AI-Powered Farm Assistant</h2>
                <p className="mt-4 text-purple-100 text-lg">
                  Get expert advice anytime. Upload photos of sick birds for instant diagnosis. 
                  Ask questions about feeding, vaccination, disease management, and more.
                </p>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <CameraIcon className="h-6 w-6 text-purple-200" />
                    <span>Upload images for visual diagnosis</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-purple-200" />
                    <span>Chat history saved for reference</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <UserGroupIcon className="h-6 w-6 text-purple-200" />
                    <span>Share conversations with your vet</span>
                  </div>
                </div>
                <Link to="/register" className="mt-8 inline-block px-6 py-3 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-colors">
                  Try AI Assistant
                </Link>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-sm text-purple-200">You asked:</p>
                    <p className="text-white">"My chickens have watery droppings. What could be wrong?"</p>
                  </div>
                  <div className="bg-purple-500/50 rounded-lg p-3">
                    <p className="text-sm text-purple-200">AI Assistant:</p>
                    <p className="text-white text-sm">Watery droppings can indicate several conditions including coccidiosis, bacterial infection, or dietary issues. I recommend checking for blood in droppings, monitoring feed and water intake, and isolating affected birds...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="mt-20">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-lg text-gray-600">Choose the plan that works for you</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Farmer Plan */}
            <div className="rounded-2xl border-2 border-green-200 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <UserGroupIcon className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Farmer</h3>
              </div>
              <div className="mb-4">
                <span className="text-4xl font-extrabold text-gray-900">KES 1,000</span>
                <span className="text-gray-500"> / month</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Everything you need to manage your farm</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>✓ Unlimited records & analytics</li>
                <li>✓ AI Assistant with image diagnosis</li>
                <li>✓ Smart reminders & scheduling</li>
                <li>✓ Online shop for selling</li>
                <li>✓ Buy from suppliers marketplace</li>
                <li>✓ Team collaboration</li>
              </ul>
              <div className="text-xs text-green-600 font-medium mb-3">14-day free trial included</div>
              <Link to="/register?type=farmer" className="block w-full text-center py-2.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700">
                Start Free Trial
              </Link>
            </div>

            {/* Supplier Plan */}
            <div className="rounded-2xl border-2 border-blue-200 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <BuildingStorefrontIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Supplier</h3>
              </div>
              <div className="mb-4">
                <span className="text-4xl font-extrabold text-gray-900">KES 1,000</span>
                <span className="text-gray-500"> / month</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Reach farmers and grow your business</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>✓ List unlimited products</li>
                <li>✓ Order management dashboard</li>
                <li>✓ Revenue tracking & reports</li>
                <li>✓ Secure wallet & withdrawals</li>
                <li>✓ Customer messaging</li>
                <li>✓ Location-based visibility</li>
              </ul>
              <div className="text-xs text-blue-600 font-medium mb-3">14-day free trial included</div>
              <Link to="/register?type=supplier" className="block w-full text-center py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700">
                Start Free Trial
              </Link>
            </div>

            {/* Customer - Free */}
            <div className="rounded-2xl border-2 border-amber-200 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCartIcon className="h-6 w-6 text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Customer</h3>
              </div>
              <div className="mb-4">
                <span className="text-4xl font-extrabold text-gray-900">Free</span>
                <span className="text-gray-500"> forever</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Buy fresh products from local farms</p>
              <ul className="space-y-2 text-sm text-gray-600 mb-6">
                <li>✓ Browse local farm shops</li>
                <li>✓ Order eggs, chicken & more</li>
                <li>✓ Pay via M-Pesa or wallet</li>
                <li>✓ Escrow payment protection</li>
                <li>✓ Order tracking</li>
                <li>✓ Favorite farms</li>
              </ul>
              <div className="text-xs text-amber-600 font-medium mb-3">No subscription required</div>
              <Link to="/register?type=client" className="block w-full text-center py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700">
                Sign Up Free
              </Link>
            </div>
          </div>
        </section>

        {/* Tutorial */}
        <section id="tutorial" className="mt-20">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold text-gray-900">How it works — Video Tutorial</h2>
            <p className="mt-2 text-gray-600">Watch a quick walkthrough of Omabra Poultry Assistant.</p>
            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200">
              <iframe
                title="Omabra Poultry Assistant Tutorial"
                src="https://www.youtube.com/embed/56Vjo4t3MBA"
                className="w-full h-[360px]"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Prefer a link?{" "}
              <a
                href="https://www.youtube.com/watch?v=56Vjo4t3MBA"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Watch on YouTube
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-gray-500">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} Omabra. All rights reserved.</div>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-gray-800">Terms</Link>
              <span className="text-gray-300">|</span>
              <Link to="/privacy" className="hover:text-gray-800">Privacy</Link>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200 pt-4">
            <div className="text-gray-600">Contact us</div>
            <div className="flex items-center gap-6">
              <a href="tel:+254743673899" className="hover:text-gray-800">Phone: +254 743673899</a>
              <a href="mailto:hello@omabrasystems.com" className="hover:text-gray-800">Email: hello@omabrasystems.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Feature = ({ title, desc }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4">
    <div className="font-medium text-gray-900">{title}</div>
    <div className="text-sm text-gray-600">{desc}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-white/90 backdrop-blur px-4 py-3 border border-white/60 shadow">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-lg font-semibold text-gray-900">{value}</div>
  </div>
);

const FlowStep = ({ number, title, desc, color }) => {
  const colors = {
    green: 'bg-green-600 text-white border-green-200',
    blue: 'bg-blue-600 text-white border-blue-200',
    amber: 'bg-amber-600 text-white border-amber-200'
  };
  
  return (
    <div className="flex flex-col items-center text-center">
      <div className={`h-12 w-12 rounded-full ${colors[color]} font-bold text-lg grid place-items-center border-4 border-white shadow-lg`}>
        {number}
      </div>
      <h4 className="mt-3 font-semibold text-gray-900 text-sm">{title}</h4>
      <p className="mt-1 text-xs text-gray-500">{desc}</p>
    </div>
  );
};

export default Landing;



